import {
  LICENCE_SECTION_LABELS,
  PRICE_LIST_CATEGORIES,
  PRICE_LIST_CATEGORY_LABELS,
  type PriceListCategory,
} from "@xop/dashboard-shared";

/**
 * How a product's features are grouped on CRM ▸ Pricing (2026-09-15).
 *
 * Features are shown under the SAME two headings everywhere a person meets
 * them — "Value Added Services", then "Platform Extensions" — the way the
 * appliance's own licence page lays them out and the way the licence builder
 * and the agreement modal pick them. The price book also carries what no
 * licence names (support plans, demo systems, training, hardware…); those
 * follow under their own category headings, in the book's order, rather than
 * being folded into a third "other" heading or hidden.
 */
export interface PricingFeatureSection<F extends { category: PriceListCategory }> {
  /** `value_added_services` / `platform_extensions`, or the category itself. */
  key: string;
  label: string;
  features: F[];
}

const SECTION_KEY: Partial<Record<PriceListCategory, string>> = {
  value_added_service: "value_added_services",
  platform_extension: "platform_extensions",
};

export function pricingFeatureSections<F extends { category: PriceListCategory }>(
  features: readonly F[],
): Array<PricingFeatureSection<F>> {
  // PRICE_LIST_CATEGORIES already starts with the two licensable ones, in
  // the appliance's order; the rest follow in the book's order.
  const known = new Set<string>(PRICE_LIST_CATEGORIES);
  const sections = PRICE_LIST_CATEGORIES.flatMap((category) => {
    // A row with a category this build does not know lands under Other rather
    // than vanishing from the list.
    const rows = features.filter(
      (feature) => feature.category === category || (category === "other" && !known.has(feature.category)),
    );
    if (rows.length === 0) return [];
    const key = SECTION_KEY[category] ?? category;
    const label =
      key === "value_added_services" || key === "platform_extensions"
        ? LICENCE_SECTION_LABELS[key]
        : PRICE_LIST_CATEGORY_LABELS[category];
    return [{ key, label, features: rows }];
  });
  return sections;
}
